import moment from "moment";

import Icon from "@assets/icon_config";
import { REMINDER_STATUS_LIST, ReminderStatus } from "@utils/constants";
import { Button, Tooltip } from "antd";
import { get, noop } from "lodash";
import { useMemo, useState } from "react";
import { useHelperApiFunctions } from "./../../../utils/customHooks";
import { useRemindersContext } from "../useReminders";
import { Spinner } from "@components/healthemSpinner/healthemSpinner";
import { useMembersContext } from "@pages/members/useMembers";
import "./reminderTooltip.scss";

export function ReminderTooltip({ reminder, onDragStart, children, placement = "leftTop" , toggleCallback = noop}: any) {
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);

  const handleTooltip = (flag: any) => {
    setIsTooltipOpen(flag);
    toggleCallback();
  };

  return (
    <>
      <Tooltip
        title={<TooltipContent {...{ reminder, setIsTooltipOpen }} />}
        trigger="click"
        placement={placement}
        open={isTooltipOpen && !onDragStart}
        onOpenChange={handleTooltip}
        overlayClassName={"calender__tooltip"}
      >
        {children}
      </Tooltip>
    </>
  );
}

function TooltipContent({ reminder, setIsTooltipOpen }: any) {
  const { setReminderToBeEdited } = useRemindersContext();

  const { handleSidebar } = useMembersContext();

  const { patientFetchLoader, fetchMemberAndNavigateToProfile } = useHelperApiFunctions();

  const { patientName, reminderDate, startTime, endTime, title, statusLabel, statusColor, reminderProgramName, recurrence } = useMemo(() => {
    const { patientName, reminderStartDate, reminderEndDate, reminderTitle: title, reminderStatus, reminderProgramName, recurrence } = reminder || {};

    const reminderDate = moment(reminderStartDate).format("MM-DD-YYYY");

    const startTime = moment(reminderStartDate).format("hh:mm a");

    const endTime = moment(reminderEndDate).format("hh:mm a");

    const status: ReminderStatus | undefined = REMINDER_STATUS_LIST.find((l) => l.value === reminderStatus);

    let statusLabel = status ? `${get(status, "label", "")} Reminder` : "";

    let statusColor = get(status, "colorCode", "");

    return { patientName, reminderDate, startTime, endTime, title, statusLabel, statusColor, reminderProgramName, recurrence };
  }, [reminder]);

  function setEditReminder() {
    setReminderToBeEdited(reminder);
  }

  function handlePatientNavigation(e: any) {
    e.stopPropagation();
    if(patientName) {
      fetchMemberAndNavigateToProfile({ patientId: reminder.patientId, successCallback: () => handleSidebar(false) });
    }
  }

  return (
    <Spinner loading={patientFetchLoader}>
      <div className="calender__tooltip__container">
        <div className="calender__tooltip__container__header">
          <div className={`calender__tooltip__container__header__patient-name ${ !patientName ? "calender__tooltip__container__header__patient-name__disable" : ""}`} onClick={handlePatientNavigation}>
            {patientName || title}
          </div>
          {recurrence && (
            <div className="calender__tooltip__container__header__recurring-icon">
              <Icon name="refreshFilled" width={20} height={20}/>
            </div>
          )}
          {reminderProgramName && <div className="calender__tooltip__container__header__care-management-icon">{reminderProgramName}</div>}
        </div>
        <Divider />
        <Body {...{ reminderDate, startTime, endTime, patientName, title }} />
        <Footer {...{ statusLabel, statusColor, setIsTooltipOpen, setEditReminder }} />
      </div>
    </Spinner>
  );
}

function Body({ reminderDate, startTime, endTime, patientName, title }: any) {
  return (
    <div className="calender__tooltip__container__body">
      <DateContent {...{ reminderDate, startTime, endTime }} />
      <Divider />
      {patientName && (
        <>
          <div className="calender__tooltip__container__body__patient">
            <span>
              <Icon name="reminderUsers" />
            </span>
            <span className="calender__tooltip__container__body__patient-label">{patientName}</span>
          </div>
          <Divider />
        </>
      )}
      {title && <TitleContent {...{ title }} />}
    </div>
  );
}

function DateContent({ reminderDate, startTime, endTime }: any) {
  return (
    <div className="calender__tooltip__container__body__date-content">
      <span className="calender__tooltip__container__body__date-content__clock-icon">
        <Icon name="clock" />
      </span>
      <span className="calender__tooltip__container__body__date-content__date">{reminderDate}</span>
      <span className="calender__tooltip__container__body__date-content__time">{`${startTime} - ${endTime}`}</span>
    </div>
  );
}

function TitleContent({ title }: any) {
  return (
    <>
      <div className="calender__tooltip__container__body__title">
        <span>
          <Icon name="title" />
        </span>
        <span className="calender__tooltip__container__body__title-label">{title}</span>
      </div>
      <Divider />
    </>
  );
}

function Footer({ statusLabel, statusColor, setIsTooltipOpen, setEditReminder }: any) {
  function handleOpen() {
    setIsTooltipOpen(false);
    setEditReminder();
  }

  return (
    <div className="calender__tooltip__container__footer">
      {statusLabel && (
        <div className="calender__tooltip__container__footer__status">
          <span className="calender__tooltip__container__footer__status-icon">
            <Icon name="square-dot" colorOfBrand={statusColor} />
          </span>
          <span className="calender__tooltip__container__footer__status-label" style={{ color: statusColor }}>
            {statusLabel}
          </span>
        </div>
      )}
      <Button type="primary" size="large" className="f16 w500 addNoteBtn" onClick={handleOpen}>
        Open
      </Button>
    </div>
  );
}

function Divider() {
  return <div className="divider-line"></div>;
}
